"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createSiteScrollBlocker = void 0;
const fastdom_1 = __importDefault(require("fastdom"));
const signals_1 = require("@wix/services-definitions/core-services/signals");
const definition_1 = require("@wix/viewer-service-environment/definition");
const definition_2 = require("@wix/viewer-service-provide-css/definition");
const createSiteScrollBlocker = ({ onSiteScrollBlockChanged, shouldBlockScrollWithoutVar } = {}, getService) => {
    const envService = getService(definition_1.EnvironmentDefinition);
    const shouldApplySiteScrollBlockMargins = !envService.experiments['specs.thunderbolt.disableSiteScrollBlockMargins'];
    const isSSR = envService.isSSR();
    const window = globalThis.window;
    let lastBlockListenerId = 0;
    const blockListeners = new Map();
    const blockSiteScrollingClassName = shouldBlockScrollWithoutVar ? 'siteScrollingBlocked' : 'blockSiteScrolling';
    const signalsService = getService(signals_1.SignalsServiceDefinition);
    const provideCssService = getService(definition_2.ProvideCssDefinition);
    const cssString = `
    .blockSiteScrolling,
    .siteScrollingBlocked {
        position: fixed;
        width: 100%;
    }

    .blockSiteScrolling:not(.disable-site-scroll-block-margin) #SITE_CONTAINER {
        margin-top: calc(var(--blocked-site-scroll-margin-top) * -1);
    }

    .blockSiteScrolling:not(.responsive):not(.disable-site-scroll-block-margin) #WIX_ADS {
        margin-top: var(--blocked-site-scroll-margin-top);
    }`;
    provideCssService.addGlobalStyle(cssString);
    const blockers = signalsService.signal([]);
    let _scrollCorrection = 0;
    let siteContainerOriginMarginTop;
    let wixAdsOriginMarginTop;
    const isScrollingBlocked = signalsService.computed(() => blockers.get().length > 0);
    const restoreScrollPosition = () => {
        window.scrollTo(0, _scrollCorrection);
    };
    const getSiteElements = () => ({
        bodyElement: document.body,
        siteContainerElement: document.getElementById('SITE_CONTAINER'),
        wixAdsElement: document.getElementById('WIX_ADS'),
    });
    const setMarginTop = (element, marginTop = '') => {
        element.style && (element.style.marginTop = marginTop);
    };
    const blockSiteScrolling = (blocker) => {
        const { bodyElement, siteContainerElement, wixAdsElement } = getSiteElements();
        fastdom_1.default.measure(() => {
            // The site should be blocked only when it's not already blocked
            if (!bodyElement.classList.contains(blockSiteScrollingClassName)) {
                _scrollCorrection = window.scrollY;
                fastdom_1.default.mutate(() => {
                    if (shouldBlockScrollWithoutVar) {
                        if (shouldApplySiteScrollBlockMargins) {
                            const marginTop = `calc(${Math.max(0.5, _scrollCorrection)}px)`;
                            siteContainerOriginMarginTop = siteContainerElement?.style.marginTop;
                            siteContainerElement && setMarginTop(siteContainerElement, `calc(${marginTop}*-1)`);
                            wixAdsOriginMarginTop = wixAdsElement?.style.marginTop;
                            wixAdsElement &&
                                !bodyElement.classList.contains('responsive') &&
                                setMarginTop(wixAdsElement, marginTop);
                        }
                    }
                    else {
                        bodyElement.style.setProperty('--blocked-site-scroll-margin-top', `${Math.max(0.5, _scrollCorrection)}px`);
                    }
                    bodyElement.classList.add(blockSiteScrollingClassName);
                });
            }
        });
        blockListeners.forEach(({ handleBlockedBy }) => handleBlockedBy && handleBlockedBy(blocker));
    };
    const unblockSiteScrolling = (blocker) => {
        const { bodyElement, siteContainerElement, wixAdsElement } = getSiteElements();
        fastdom_1.default.mutate(() => {
            bodyElement.classList.remove(blockSiteScrollingClassName);
            if (shouldBlockScrollWithoutVar) {
                if (shouldApplySiteScrollBlockMargins) {
                    siteContainerElement && setMarginTop(siteContainerElement, siteContainerOriginMarginTop);
                    wixAdsElement &&
                        !bodyElement.classList.contains('responsive') &&
                        setMarginTop(wixAdsElement, wixAdsOriginMarginTop);
                }
            }
            else {
                bodyElement.style.removeProperty('--blocked-site-scroll-margin-top');
            }
            restoreScrollPosition();
        });
        blockListeners.forEach(({ handleUnblockedBy }) => handleUnblockedBy && handleUnblockedBy(blocker));
    };
    const addBlocker = (blocker) => {
        blockers.set(!blockers.get().includes(blocker) ? [...blockers.get(), blocker] : blockers.get());
        onSiteScrollBlockChanged && onSiteScrollBlockChanged(isScrollingBlocked.get());
        // The site should be blocked only when there's one blocker,
        // otherwise it's already blocked (more than one) or doesn't need to be blocked (zero)
        const shouldBlock = blockers.get().length === 1;
        if (shouldBlock) {
            blockSiteScrolling(blocker);
        }
    };
    const removeBlocker = (blocker) => {
        const [activeBlocker] = blockers.get();
        blockers.set(blockers.get().filter(b => b !== blocker));
        const [newActiveBlocker] = blockers.get();
        onSiteScrollBlockChanged && onSiteScrollBlockChanged(isScrollingBlocked.get());
        // The active blocker changes if we remove the blockers not from the end to start.
        // For example, removing from start to end, the active blocker should be adjusted each time (because the first blocker changes)
        const hasActiveBlockerChanged = activeBlocker !== newActiveBlocker;
        if (hasActiveBlockerChanged) {
            if (newActiveBlocker) {
                blockSiteScrolling(blocker);
            }
            else {
                unblockSiteScrolling(blocker);
            }
        }
    };
    const setSiteScrollingBlocked = (blocked, compId) => {
        if (isSSR) {
            return;
        }
        return blocked ? addBlocker(compId) : removeBlocker(compId);
    };
    return {
        setSiteScrollingBlocked,
        registerScrollBlockedListener(listener) {
            const listenerId = ++lastBlockListenerId;
            blockListeners.set(listenerId, listener);
            return listenerId;
        },
        unRegisterScrollBlockedListener(listenerId) {
            blockListeners.delete(listenerId);
        },
        isScrollingBlocked: () => isScrollingBlocked.get(),
    };
};
exports.createSiteScrollBlocker = createSiteScrollBlocker;
//# sourceMappingURL=siteScrollBlocker.js.map